import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COUNTRIES,
  IDP_LABELS,
  ORG_ALLOWED_IDPS,
  normaliseCountry,
  ORG_RESPONSIBILITY_ROLES,
  type Org,
  type OrgAllowedIdp,
  type OrgMember,
  type OrgProfile,
  type OrgResponsibility,
  type OrgResponsibilityRoleKey,
  type UpdateOrgInput,
  type UpdateOrgProfileInput,
} from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Textarea,
  formatDate,
  useToast,
} from '../../components/ui.js';

const MAX_LOGO_BYTES = 50 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg'];

/** "Field: message" for the first zod issue, so the toast names what to fix. */
function validationMessage(err: ApiRequestError): string | null {
  if (err.code !== 'validation_failed') return null;
  const issues = err.details?.issues;
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const first = issues[0] as { path?: string; message?: string };
  if (!first.message) return null;
  return first.path ? `${first.path}: ${first.message}` : first.message;
}

/** Ticked providers for the form: null (any) shows every provider ticked. */
function idpsFromOrg(org: Org): OrgAllowedIdp[] {
  const allowed = org.allowedIdps;
  return allowed === null
    ? [...ORG_ALLOWED_IDPS]
    : ORG_ALLOWED_IDPS.filter((idp) => allowed.includes(idp));
}

export function OrgSettingsPage() {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = org.role === 'admin';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['org', org.orgId],
    queryFn: () => apiFetch<Org>(`/orgs/${org.orgId}`),
    enabled: isAdmin,
  });
  const profileQuery = useQuery({
    queryKey: ['org-profile', org.orgId],
    queryFn: () => apiFetch<OrgProfile>(`/orgs/${org.orgId}/profile`),
    enabled: isAdmin,
  });
  const responsibilitiesQuery = useQuery({
    queryKey: ['org-responsibilities', org.orgId],
    queryFn: () =>
      apiFetch<{ items: OrgResponsibility[] }>(`/orgs/${org.orgId}/responsibilities`),
    enabled: isAdmin,
  });
  const membersQuery = useQuery({
    queryKey: ['members', org.orgId],
    queryFn: () => apiFetch<OrgMember[]>(`/orgs/${org.orgId}/members`),
    enabled: isAdmin,
  });

  const [name, setName] = useState('');
  const [allowDomainJoin, setAllowDomainJoin] = useState(false);
  // Ticked providers; null on the API (any provider) shows as all ticked.
  const [allowedIdps, setAllowedIdps] = useState<OrgAllowedIdp[]>([...ORG_ALLOWED_IDPS]);
  // Organisation AI switch (every AI feature; default on).
  const [aiEnabled, setAiEnabled] = useState(true);
  // Dirty guard: background refetches (e.g. after a logo upload invalidates
  // ['org']) must not clobber unsaved edits, so hydrate only while pristine.
  const orgFormDirty = useRef(false);
  useEffect(() => {
    if (data && !orgFormDirty.current) {
      setName(data.name);
      setAllowDomainJoin(data.allowDomainJoin);
      setAllowedIdps(idpsFromOrg(data));
      setAiEnabled(data.aiEnabled);
    }
  }, [data]);

  // Organisation logo: the endpoint returns binary (404 when unset), so fetch
  // it as a blob with credentials and preview via an object URL.
  const logoQuery = useQuery({
    queryKey: ['org-logo', org.orgId],
    queryFn: async (): Promise<Blob | null> => {
      const res = await fetch(apiUrl(`/orgs/${org.orgId}/logo`), { credentials: 'include' });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('Could not load the organisation logo');
      return res.blob();
    },
    enabled: isAdmin,
  });
  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  useEffect(() => {
    const blob = logoQuery.data;
    if (!blob) {
      setLogoSrc(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setLogoSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [logoQuery.data]);

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const uploadLogo = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiFetch<{ hasLogo: boolean }>(`/orgs/${org.orgId}/logo`, {
        method: 'PUT',
        formData,
      });
    },
    onSuccess: () => {
      setLogoFile(null);
      if (fileInput.current) fileInput.current.value = '';
      void queryClient.invalidateQueries({ queryKey: ['org-logo', org.orgId] });
      void queryClient.invalidateQueries({ queryKey: ['org', org.orgId] });
      toast('Logo uploaded.', 'success');
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not upload the logo', 'error'),
  });

  const removeLogo = useMutation({
    mutationFn: () => apiFetch<{ hasLogo: boolean }>(`/orgs/${org.orgId}/logo`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-logo', org.orgId] });
      toast('Logo removed.', 'success');
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not remove the logo', 'error'),
  });

  const [businessName, setBusinessName] = useState('');
  const [companyDescription, setCompanyDescription] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [hasEmployees, setHasEmployees] = useState(false);
  const [hqInEu, setHqInEu] = useState(false);
  const profileFormDirty = useRef(false);
  useEffect(() => {
    const profile = profileQuery.data;
    if (profile && !profileFormDirty.current) {
      setBusinessName(profile.businessName);
      setCompanyDescription(profile.companyDescription);
      setAddressLine(profile.addressLine);
      setPostcode(profile.postcode);
      setCity(profile.city);
      // Legacy rows hold free text; map onto the list where we can, otherwise
      // keep the raw value so the admin sees what was there and reselects.
      setCountry(normaliseCountry(profile.country) ?? profile.country);
      setRegistrationNumber(profile.registrationNumber);
      setCompanyEmail(profile.companyEmail);
      setHasEmployees(profile.hasEmployees);
      setHqInEu(profile.hqInEu);
    }
  }, [profileQuery.data]);

  // roleKey → userId ('' = unassigned)
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const assignmentsDirty = useRef(false);
  useEffect(() => {
    const items = responsibilitiesQuery.data?.items;
    if (items && !assignmentsDirty.current) {
      const next: Record<string, string> = {};
      for (const item of items) next[item.roleKey] = item.userId ?? '';
      setAssignments(next);
    }
  }, [responsibilitiesQuery.data]);

  const save = useMutation({
    mutationFn: (body: UpdateOrgInput) =>
      apiFetch<Org>(`/orgs/${org.orgId}`, { method: 'PATCH', body }),
    onSuccess: (saved) => {
      orgFormDirty.current = false;
      setName(saved.name);
      setAllowDomainJoin(saved.allowDomainJoin);
      setAllowedIdps(idpsFromOrg(saved));
      setAiEnabled(saved.aiEnabled);
      void queryClient.invalidateQueries({ queryKey: ['org', org.orgId] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      toast('Organisation settings saved.', 'success');
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not save settings', 'error'),
  });

  const saveProfile = useMutation({
    mutationFn: (body: UpdateOrgProfileInput) =>
      apiFetch<OrgProfile>(`/orgs/${org.orgId}/profile`, { method: 'PUT', body }),
    onSuccess: () => {
      profileFormDirty.current = false;
      void queryClient.invalidateQueries({ queryKey: ['org-profile', org.orgId] });
      toast('General information saved.', 'success');
    },
    onError: (err) =>
      toast(
        err instanceof ApiRequestError
          ? validationMessage(err) ?? err.message
          : 'Could not save general information',
        'error',
      ),
  });

  const saveResponsibilities = useMutation({
    mutationFn: () =>
      apiFetch<{ items: OrgResponsibility[] }>(`/orgs/${org.orgId}/responsibilities`, {
        method: 'PUT',
        body: {
          assignments: ORG_RESPONSIBILITY_ROLES.map((role) => ({
            roleKey: role.key as OrgResponsibilityRoleKey,
            userId: assignments[role.key] ? assignments[role.key] : null,
          })),
        },
      }),
    onSuccess: () => {
      assignmentsDirty.current = false;
      void queryClient.invalidateQueries({ queryKey: ['org-responsibilities', org.orgId] });
      toast('Responsible individuals saved.', 'success');
    },
    onError: (err) =>
      toast(
        err instanceof ApiRequestError ? err.message : 'Could not save responsible individuals',
        'error',
      ),
  });

  if (!isAdmin) {
    return (
      <EmptyState
        title="Admins only"
        message="Organisation settings can only be managed by organisation admins."
      />
    );
  }
  if (isError || profileQuery.isError || responsibilitiesQuery.isError) {
    return (
      <div>
        <PageHeader title="Organisation settings" />
        <EmptyState
          title="Could not load settings"
          message="Something went wrong loading organisation settings — please try again shortly."
          action={
            <Button
              onClick={() => {
                void refetch();
                void profileQuery.refetch();
                void responsibilitiesQuery.refetch();
              }}
            >
              Retry
            </Button>
          }
        />
      </div>
    );
  }
  if (isLoading || !data || profileQuery.isLoading || responsibilitiesQuery.isLoading) {
    return <LoadingPane label="Loading settings" />;
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      name: name.trim(),
      allowDomainJoin,
      // Every provider ticked = no restriction.
      allowedIdps: allowedIdps.length === ORG_ALLOWED_IDPS.length ? null : allowedIdps,
      aiEnabled,
    });
  };

  const toggleIdp = (idp: OrgAllowedIdp, on: boolean) => {
    orgFormDirty.current = true;
    setAllowedIdps((prev) => {
      const next = on ? [...new Set([...prev, idp])] : prev.filter((p) => p !== idp);
      // At least one provider must stay allowed.
      return next.length === 0 ? prev : ORG_ALLOWED_IDPS.filter((p) => next.includes(p));
    });
  };

  const selectLogo = (file: File | null) => {
    if (!file) {
      setLogoFile(null);
      return;
    }
    if (!LOGO_TYPES.includes(file.type)) {
      toast('Logo must be a PNG or JPEG image.', 'error');
      if (fileInput.current) fileInput.current.value = '';
      setLogoFile(null);
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast('Logo must be 50 MB or smaller.', 'error');
      if (fileInput.current) fileInput.current.value = '';
      setLogoFile(null);
      return;
    }
    setLogoFile(file);
  };

  const submitProfile = (e: FormEvent) => {
    e.preventDefault();
    saveProfile.mutate({
      businessName: businessName.trim(),
      companyDescription: companyDescription.trim(),
      addressLine: addressLine.trim(),
      postcode: postcode.trim(),
      city: city.trim(),
      country: country.trim(),
      registrationNumber: registrationNumber.trim(),
      companyEmail: companyEmail.trim(),
      hasEmployees,
      hqInEu,
    });
  };

  const members = membersQuery.data ?? [];
  const countryNotInList = country !== '' && !COUNTRIES.includes(country);

  return (
    <div>
      <PageHeader title="Organisation settings" subtitle={`Profile and access for ${data.name}`} />

      <Card title="Profile">
        <form onSubmit={submit}>
          <Field label="Organisation name" required>
            {(props) => (
              <Input
                {...props}
                required
                minLength={2}
                value={name}
                onChange={(e) => {
                  orgFormDirty.current = true;
                  setName(e.target.value);
                }}
              />
            )}
          </Field>
          {logoSrc && (
            <div style={{ marginBottom: 12 }}>
              <img
                src={logoSrc}
                alt="Current organisation logo"
                style={{
                  maxHeight: 80,
                  maxWidth: '100%',
                  border: '1px solid var(--color-border)',
                  borderRadius: 4,
                  padding: 4,
                  background: '#fff',
                }}
              />
            </div>
          )}
          {!logoSrc && (
            <p style={{ color: 'var(--color-text-muted)' }}>No logo uploaded yet.</p>
          )}
          <Field label="Organisation logo" hint="PNG or JPEG, up to 50 MB">
            {(props) => (
              <input
                {...props}
                className="input"
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => selectLogo(e.target.files?.[0] ?? null)}
              />
            )}
          </Field>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Button
              variant="secondary"
              disabled={!logoFile}
              loading={uploadLogo.isPending}
              onClick={() => logoFile && uploadLogo.mutate(logoFile)}
            >
              Upload logo
            </Button>
            {logoSrc && (
              <Button
                variant="danger"
                loading={removeLogo.isPending}
                onClick={() => removeLogo.mutate()}
              >
                Remove logo
              </Button>
            )}
          </div>
          <div className="field">
            <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={allowDomainJoin}
                onChange={(e) => {
                  orgFormDirty.current = true;
                  setAllowDomainJoin(e.target.checked);
                }}
              />
              Allow colleagues to join automatically by email domain
            </label>
            <span className="field-hint">
              Anyone signing in with an email ending in <strong>@{data.domain ?? 'your-domain'}</strong>{' '}
              will join this organisation as a contributor. Leave off for invitation-only membership.
            </span>
          </div>
          <fieldset className="field" style={{ border: 'none', padding: 0, margin: '0 0 16px' }}>
            <legend className="field-label" style={{ padding: 0, marginBottom: 4 }}>
              Allowed sign-in providers
            </legend>
            {ORG_ALLOWED_IDPS.map((idp) => (
              <label
                key={idp}
                className="field-label"
                style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 500 }}
              >
                <input
                  type="checkbox"
                  checked={allowedIdps.includes(idp)}
                  disabled={allowedIdps.length === 1 && allowedIdps.includes(idp)}
                  onChange={(e) => toggleIdp(idp, e.target.checked)}
                />
                {IDP_LABELS[idp]}
              </label>
            ))}
            <span className="field-hint">
              Members can only use the ticked providers to sign in; tick both to allow either.
              Members signed in another way see a message asking them to sign in again. API tokens
              are not affected.
            </span>
          </fieldset>
          <div className="field">
            <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={aiEnabled}
                onChange={(e) => {
                  orgFormDirty.current = true;
                  setAiEnabled(e.target.checked);
                }}
              />
              Enable AI assistance
            </label>
            <span className="field-hint">
              AI suggestions in assessments, AI document and register import, AI assessor pre-checks
              and AI internal audits. When off, the AI tab and buttons are hidden for everyone in
              this organisation.
            </span>
          </div>
          <Button type="submit" loading={save.isPending}>
            Save changes
          </Button>
        </form>
      </Card>

      <Card title="General information">
        {/* Every control in this form is a profile field, so any change event
            bubbling here marks the form dirty and pauses query hydration. */}
        <form
          onSubmit={submitProfile}
          onChange={() => {
            profileFormDirty.current = true;
          }}
        >
          <Field label="Business name">
            {(props) => (
              <Input {...props} value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
            )}
          </Field>
          <Field label="Company description">
            {(props) => (
              <Textarea
                {...props}
                rows={4}
                value={companyDescription}
                onChange={(e) => setCompanyDescription(e.target.value)}
              />
            )}
          </Field>
          <Field label="Company address">
            {(props) => (
              <Input {...props} value={addressLine} onChange={(e) => setAddressLine(e.target.value)} />
            )}
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
            <Field label="Post code">
              {(props) => (
                <Input {...props} value={postcode} onChange={(e) => setPostcode(e.target.value)} />
              )}
            </Field>
            <Field label="City">
              {(props) => <Input {...props} value={city} onChange={(e) => setCity(e.target.value)} />}
            </Field>
            <Field
              label="Country"
              hint={
                countryNotInList
                  ? `"${country}" is not in the list — please choose a country before saving.`
                  : undefined
              }
            >
              {(props) => (
                <Select {...props} value={country} onChange={(e) => setCountry(e.target.value)}>
                  <option value="">Select…</option>
                  {countryNotInList && (
                    <option value={country}>{country} (not in list — please choose)</option>
                  )}
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 16px' }}>
            <Field label="Registration number">
              {(props) => (
                <Input
                  {...props}
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                />
              )}
            </Field>
            <Field label="Company email">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={companyEmail}
                  onChange={(e) => setCompanyEmail(e.target.value)}
                />
              )}
            </Field>
          </div>
          <div className="field">
            <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={hasEmployees}
                onChange={(e) => setHasEmployees(e.target.checked)}
              />
              We have employees
            </label>
          </div>
          <div className="field">
            <label className="field-label" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={hqInEu} onChange={(e) => setHqInEu(e.target.checked)} />
              Headquartered in the EU
            </label>
          </div>
          <Button type="submit" loading={saveProfile.isPending}>
            Save general information
          </Button>
        </form>
      </Card>

      <Card title="Responsible individuals">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Assign an organisation member to each responsibility. Leave a role unassigned if it does
          not apply.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveResponsibilities.mutate();
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0 16px' }}>
            {ORG_RESPONSIBILITY_ROLES.map((role) => (
              <Field key={role.key} label={role.label}>
                {(props) => (
                  <Select
                    {...props}
                    value={assignments[role.key] ?? ''}
                    onChange={(e) => {
                      assignmentsDirty.current = true;
                      setAssignments((prev) => ({ ...prev, [role.key]: e.target.value }));
                    }}
                  >
                    <option value="">— Unassigned —</option>
                    {members.map((m) => (
                      <option key={m.user.id} value={m.user.id}>
                        {m.user.name} ({m.user.email})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ))}
          </div>
          <Button type="submit" loading={saveResponsibilities.isPending}>
            Save responsibilities
          </Button>
        </form>
      </Card>

      <Card title="Details">
        <p style={{ color: 'var(--color-text-muted)' }}>
          Organisation ID <code>{data.id}</code> · slug <code>{data.slug}</code> · created{' '}
          {formatDate(data.createdAt)} · plan <strong>{data.plan}</strong>
        </p>
      </Card>
    </div>
  );
}
